import { ReactElement, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Box, Button, Chip, Divider, IconButton, Menu, MenuItem, Paper, Rating, Stack, Tab, Tabs, Typography, Alert, AlertTitle, Tooltip } from "@mui/material";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useReactToPrint } from "react-to-print";
import OrderDetailsTab from "@/components/OrderDetails/OrderDetails";
import OrderClientTab from "@/components/client-details/ClientDetails";
import ReceiptCustomer from "@/components/ReceiptCustomer.template";
import ReceiptKitchen from "@/components/ReceiptKitchen.template";
import { Order } from "@/types";
import { OrderReview } from "@/types/surveys";
import { Cancel01Icon, ChefHatIcon, PrinterIcon, UserIcon, ReloadIcon, Link02Icon, Scooter03Icon, BubbleChatFavouriteIcon as ChatFavouriteIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import useResumeOrder from "../hooks/useResumeOrder";
import useSnackbar from "@/hooks/useSnackbar";
import useOrganizations from "@/features/organizations/useOrganizations";
import { useFetchDriversQuery } from "@/api/driversApi";

const INITIAL_TAB = "details";

type TabsVariant = "standard" | "scrollable" | "fullWidth";

type OrderInfoTabsProps = {
  order: Order;
  tabsVariant?: TabsVariant;
  isPage?: boolean;
  onClose?: () => void;
};

const OrderInfoTabs = ({ order, tabsVariant = "standard", isPage = false, onClose }: OrderInfoTabsProps) => {
  const { organizationId } = useParams();
  const ReceiptCustomerRef = useRef(null);
  const ReceiptKitchenRef = useRef(null);
  const [activeTab, setActiveTab] = useState<keyof typeof tabs>(INITIAL_TAB);
  const [openSnackbar] = useSnackbar();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { handleResumeOrder } = useResumeOrder();
  const { currentOrganization } = useOrganizations();
  const { data: drivers = [] } = useFetchDriversQuery(organizationId || "");
  const [driversAnchorEl, setDriversAnchorEl] = useState<null | HTMLElement>(null);
  const driversMenuOpen = Boolean(driversAnchorEl);

  const selectedDriverIds = currentOrganization?.settings?.drivers || [];

  const availableDrivers = drivers.filter(
    (d) => selectedDriverIds.includes(d.id)
  );

  const canResume = order.status === "rejected" && order.rejectedReason === "timeout";

  const onResume = () => handleResumeOrder(order, onClose);

  const tabs: Record<
    string,
    {
      label: string;
      component: ReactElement;
    }
  > = {
    details: {
      label: "Pedido",
      component: <OrderDetailsTab {...order} />,
    },
    clientInfo: {
      label: "Cliente",
      component: <OrderClientTab {...order} />,
    },
    ...(order.orderReview
      ? {
          customerExperience: {
            label: "Experiencia del cliente",
            component: (
              <CustomerExperienceTab
                orderReview={order.orderReview}
                organizationId={organizationId}
              />
            ),
          },
        }
      : {}),
  };

  const resolvedActiveTab = tabs[activeTab] ? activeTab : INITIAL_TAB;

  const handleChange = (_event: React.SyntheticEvent, newValue: keyof typeof tabs) => {
    setActiveTab(newValue);
  };

  const handlePrintTicket = useReactToPrint({
    contentRef: ReceiptCustomerRef,
    documentTitle: "Factura",
    onAfterPrint: () => setAnchorEl(null),
  });

  const handlePrintReceiptKitchen = useReactToPrint({
    contentRef: ReceiptKitchenRef,
    documentTitle: "Comanda",
    onAfterPrint: () => setAnchorEl(null),
  });

  const handleContactDriver = (driver: any) => {
    const message = `Nueva solicitud de delivery de ${currentOrganization?.title}. El pedido es el número ${order.number}.\n\n Detalles del pedido: https://bitelink.app/comercios/${organizationId}/orders/${order.id}`;

    const phone = `505${driver.phone}`;

    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;

    window.open(url, "_blank");
  };

  return (
    <Stack sx={{ height: "100%" }}>
      <div>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            padding: 2,
            paddingBottom: 0,
          }}
        >
          {!isPage ? (
            <>
              <Typography variant="h2">{`Pedido / ${order.number}`}</Typography>
              <Stack direction="row" alignItems="center" spacing={2}>
                {canResume && (
                  <Tooltip title="Retomar pedido" placement="bottom">
                    <IconButton color="primary" size="large" component={Paper} elevation={2} onClick={onResume} sx={{ backgroundColor: "#ffffff" }}>
                      <HugeiconsIcon icon={ReloadIcon} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Contactar repartidores" placement="bottom">
                  <IconButton
                    color="primary"
                    size="large"
                    component={Paper}
                    elevation={2}
                    sx={{ backgroundColor: "#ffffff" }}
                    onClick={(e) => setDriversAnchorEl(e.currentTarget)}
                  >
                    <HugeiconsIcon icon={Scooter03Icon} />
                  </IconButton>
                </Tooltip>
                <Menu id="drivers-menu" anchorEl={driversAnchorEl} open={driversMenuOpen} onClose={() => setDriversAnchorEl(null)}>
                  {availableDrivers.map((driver) => (
                    <MenuItem
                      key={driver.id}
                      onClick={() => {
                        handleContactDriver(driver);
                        setDriversAnchorEl(null);
                      }}
                    >
                      {driver.name}
                    </MenuItem>
                  ))}
                </Menu>
                <Tooltip title="Copiar enlace" placement="bottom">
                  <IconButton
                    color="primary"
                    size="large"
                    component={Paper}
                    elevation={2}
                    onClick={async () => {
                      const url = `bitelink.app/comercios/${organizationId}/orders/${order.id}`;
                      try {
                        await navigator.clipboard.writeText(url);
                        openSnackbar("Enlace copiado al portapapeles", "info");
                      } catch (err) {
                        console.error("Error al copiar: ", err);
                      }
                    }}
                    sx={{ backgroundColor: "#ffffff" }}
                  >
                    <HugeiconsIcon icon={Link02Icon} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Imprimir" placement="bottom">
                  <IconButton color="primary" size="large" component={Paper} elevation={2} onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ backgroundColor: "#ffffff" }}>
                    <HugeiconsIcon icon={PrinterIcon} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cerrar" placement="bottom">
                  <IconButton color="primary" size="large" component={Paper} elevation={2} onClick={onClose} sx={{ backgroundColor: "#ffffff" }}>
                    <HugeiconsIcon icon={Cancel01Icon} />
                  </IconButton>
                </Tooltip>
                <Menu id="printer-menu" anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
                  <MenuItem onClick={handlePrintTicket}>Factura</MenuItem>
                  <MenuItem onClick={handlePrintReceiptKitchen}>Comanda</MenuItem>
                </Menu>
              </Stack>
            </>
          ) : null}
        </Stack>

        <Tabs value={resolvedActiveTab} onChange={handleChange} variant={tabsVariant} sx={{ paddingX: 2 }}>
          {Object.entries(tabs).map(([tabKey, tab]) => (
            <Tab key={tabKey} label={tab.label} value={tabKey} />
          ))}
        </Tabs>
        <Divider sx={{ margin: 0 }} />
      </div>
      {isPage ? (
        <Stack direction="row" spacing={2} alignItems={"center"} justifyContent={"center"}>
          <Button fullWidth color="primary" onClick={handlePrintReceiptKitchen} component={Paper} elevation={2} startIcon={<HugeiconsIcon icon={ChefHatIcon} />}>
            Cocina
          </Button>
          <Button variant="outlined" fullWidth color="primary" onClick={handlePrintTicket} component={Paper} elevation={2} startIcon={<HugeiconsIcon icon={UserIcon} />}>
            Cliente
          </Button>
        </Stack>
      ) : null}
      <Stack spacing={2} flex={1} height={"100%"} padding={isPage ? 0 : 2}>
        {order.status === "rejected" && order.rejectedReason ? (
          <Alert
            severity="error"
            action={
              canResume ? (
                <Button size="small" color="inherit" variant="outlined" onClick={onResume}>
                  Retomar pedido
                </Button>
              ) : null
            }
          >
            <AlertTitle>Pedido Rechazado</AlertTitle>
            <Typography variant="body1">
              {order.rejectedReason === "timeout" ? "Auto rechazada por no haberse atendido a tiempo." : order.rejectedReason}
            </Typography>
          </Alert>
        ) : null}
        {tabs[resolvedActiveTab].component}
      </Stack>

      <div style={{ display: "none" }}>
        <div ref={ReceiptCustomerRef} style={{ height: "100%", width: "100%", display: "flex", flex: 1 }}>
          <ReceiptCustomer order={order} />
        </div>
        <div ref={ReceiptKitchenRef} style={{ height: "100%", width: "100%", display: "flex", flex: 1 }}>
          <ReceiptKitchen order={order} />
        </div>
      </div>
    </Stack>
  );
};

const CustomerExperienceTab = ({
  orderReview,
  organizationId,
}: {
  orderReview: OrderReview;
  organizationId?: string;
}) => {
  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Stack spacing={2.5}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1.5}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "10px",
                  backgroundColor: "rgba(1, 95, 238, 0.1)",
                  color: "#015FEE",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <HugeiconsIcon icon={ChatFavouriteIcon} size={22} />
              </Box>
              <Stack>
                <Typography variant="h4" fontWeight={700}>
                  Evaluación Post-Pedido
                </Typography>
                {orderReview.createdAt ? (
                  <Typography variant="caption" color="text.secondary">
                    Respondida el{" "}
                    {format(
                      new Date(orderReview.createdAt),
                      "dd 'de' MMMM 'de' yyyy, hh:mm a",
                      { locale: es }
                    )}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>

            {organizationId && orderReview.surveyId ? (
              <Button
                variant="outlined"
                size="small"
                component={Link}
                to={`/comercios/${organizationId}/surveys/${orderReview.surveyId}`}
              >
                Ver encuesta
              </Button>
            ) : null}
          </Stack>

          <Divider />

          {/* 1. Calificación general */}
          <Stack spacing={1}>
            <Typography variant="subtitle2" color="text.secondary">
              ¿Cómo calificarías tu experiencia general con tu pedido?
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="h2" fontWeight={700}>
                {orderReview.rating ? orderReview.rating.toFixed(1) : "—"}
              </Typography>
              <Rating readOnly value={orderReview.rating || 0} size="large" />
              {orderReview.rating ? (
                <Chip
                  size="small"
                  color={
                    orderReview.rating >= 4
                      ? "success"
                      : orderReview.rating === 3
                        ? "warning"
                        : "error"
                  }
                  label={`${orderReview.rating} de 5 ★`}
                />
              ) : null}
            </Stack>
          </Stack>

          <Divider />

          {/* 2. Calidad de los alimentos */}
          <Stack spacing={0.75}>
            <Typography variant="subtitle2" color="text.secondary">
              ¿Cómo estuvieron tus alimentos?
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {orderReview.foodQuality || "Sin respuesta registrada"}
            </Typography>
          </Stack>

          <Divider />

          {/* 3. Comentario adicional */}
          <Stack spacing={0.75}>
            <Typography variant="subtitle2" color="text.secondary">
              ¿Tienes algún comentario o sugerencia adicional?
            </Typography>
            {orderReview.comment ? (
              <Paper
                variant="outlined"
                sx={{ p: 2, borderRadius: 2, backgroundColor: "grey.50" }}
              >
                <Typography variant="body1">"{orderReview.comment}"</Typography>
              </Paper>
            ) : (
              <Typography variant="body2" color="text.secondary">
                El cliente no dejó comentarios adicionales.
              </Typography>
            )}
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default OrderInfoTabs;
